import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Container, Card, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import logo from '../../../../images/logo.png';
import { getCompanyInfo, lookupCustomerByDocument } from '../../../../services/PublicBookingService';
import StepBranch from './steps/StepBranch';
import StepService from './steps/StepService';
import StepEmployee from './steps/StepEmployee';
import StepDateTime from './steps/StepDateTime';
import StepCustomerInfo from './steps/StepCustomerInfo';
import StepConfirmation from './steps/StepConfirmation';
import './PublicBooking.css';
import ClientTabBar from '../shared/ClientTabBar';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import { formatNumberWithTwoDecimals } from '../../../../utils/FormatUtils';
import { clearStoredCustomerDocument, getStoredCustomerDocument, setStoredCustomerDocument } from '../../../../utils/PublicBookingStorage';
import { hasActiveOnlineBookingBlock, lookupStatusFields } from '../../../../utils/PublicBookingCustomer';
import ImageWithSkeleton from '../../../components/Custom/Common/ImageWithSkeleton';
import AddBookingToHomeScreenHint from './AddBookingToHomeScreenHint';

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

// Véu sobre o banner: o texto do hero é branco e precisa de contraste em cima de qualquer
// imagem. Uniforme, e não em degradê, para a base do hero não escurecer até quase preto.
// Duas paradas com a mesma cor é como se aplica uma cor chapada em background-image.
const getHeroStyle = (companyInfo) => {
    if (!companyInfo?.bannerUrl) {
        // Sem banner, só a cor da empresa sobrescreve o fundo chapado de .booking-hero.
        return companyInfo?.headerColor ? { background: companyInfo.headerColor } : undefined;
    }

    return {
        backgroundColor: companyInfo?.headerColor || '#232c3a',
    };
};

const shouldShowBookingLogo = (companyInfo) => companyInfo?.showLogoOnBookingPage !== false;
const shouldShowBookingName = (companyInfo) => companyInfo?.showCompanyNameOnBookingPage !== false;

const formatBookingContextDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('pt-BR', {
        weekday: 'short',
        day: '2-digit',
        month: 'short'
    }).format(date);
};

/**
 * Saudação pelo horário do aparelho do cliente, que é o horário que ele enxerga — o fuso da
 * barbearia não muda o que é "boa tarde" para quem está olhando a tela.
 */
const getGreeting = (date = new Date()) => {
    const hour = date.getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
};

/**
 * A identificação abre o fluxo de propósito: é ela que revela o vínculo com o clube, e sem isso
 * o assinante escolhia o serviço vendo preço cheio e só descobria o benefício na confirmação —
 * tarde demais para trocar por um serviço coberto pelo plano. Ela não depende da filial: o
 * lookup por CPF é por empresa.
 */
const STEPS = {
    CUSTOMER: 0,
    BRANCH: 1,
    SERVICE: 2,
    EMPLOYEE: 3,
    DATETIME: 4,
    CONFIRMATION: 5
};

const STEP_ROUTE = {
    [STEPS.CUSTOMER]: 'customer',
    [STEPS.BRANCH]: 'branch',
    [STEPS.SERVICE]: 'services',
    [STEPS.EMPLOYEE]: 'employee',
    [STEPS.DATETIME]: 'date-time',
    [STEPS.CONFIRMATION]: 'review'
};
const ROUTE_STEP = Object.fromEntries(Object.entries(STEP_ROUTE).map(([step, route]) => [route, Number(step)]));
const LEGACY_ROUTE_STEP = {
    identificacao: STEPS.CUSTOMER,
    filial: STEPS.BRANCH,
    servicos: STEPS.SERVICE,
    profissional: STEPS.EMPLOYEE,
    'data-horario': STEPS.DATETIME,
    confirmacao: STEPS.CONFIRMATION
};
const BOOKING_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

const readBookingDraft = (companyUuid) => {
    if (!companyUuid) return null;
    const key = `public-booking-draft:${companyUuid}`;
    try {
        const draft = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (!draft) return null;
        if (!draft.savedAt || Date.now() - draft.savedAt > BOOKING_DRAFT_TTL_MS) {
            sessionStorage.removeItem(key);
            return null;
        }
        return draft;
    } catch {
        return null;
    }
};

const PublicBookingPage = () => {
    const { t } = useTranslation();
    const { companyUuid, step: routeStep } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    // O retorno explícito de "Meus agendamentos" inicia um fluxo limpo, mesmo se houver
    // confirmação ou rascunho salvo na mesma aba.
    const [initialDraft] = useState(() => (
        location.state?.startBookingFresh ? null : readBookingDraft(companyUuid)
    ));
    // /confirmation é a tela final; /confirmed permanece como alias para links já compartilhados.
    const isConfirmedRoute = routeStep === 'confirmation' || routeStep === 'confirmed';

    // Estado geral
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const currentStep = isConfirmedRoute
        ? STEPS.CONFIRMATION
        : ROUTE_STEP[routeStep] ?? LEGACY_ROUTE_STEP[routeStep] ?? initialDraft?.currentStep ?? STEPS.CUSTOMER;

    // Dados da empresa
    const [companyInfo, setCompanyInfo] = useState(null);

    // Seleções do wizard
    const [selectedBranch, setSelectedBranch] = useState(initialDraft?.selectedBranch || null);
    const [selectedServices, setSelectedServices] = useState(initialDraft?.selectedServices || []);
    const [selectedEmployee, setSelectedEmployee] = useState(initialDraft?.selectedEmployee || null);
    const [selectedDate, setSelectedDate] = useState(() => {
        const date = initialDraft?.selectedDate ? new Date(initialDraft.selectedDate) : null;
        return date && !Number.isNaN(date.getTime()) ? date : null;
    });
    const [selectedTime, setSelectedTime] = useState(initialDraft?.selectedTime || null);
    const [customerData, setCustomerData] = useState(initialDraft?.customerData || {
        firstName: '',
        lastName: '',
        phone: '',
        document: ''
    });

    // Fechar os avisos do topo vale para a visita atual e não fica gravado: ao identificar
    // outro cliente eles voltam. A pendência de mensalidade, porém, é um bloqueio de negócio
    // e precisa continuar visível enquanto impedir o agendamento.
    const [benefitDaysDismissed, setBenefitDaysDismissed] = useState(false);
    const [penaltyDismissed, setPenaltyDismissed] = useState(false);

    useEffect(() => {
        setBenefitDaysDismissed(false);
        setPenaltyDismissed(false);
    }, [customerData.document]);

    // Dados do agendamento criado
    const [appointmentResult, setAppointmentResult] = useState(initialDraft?.appointmentResult || null);

    useEffect(() => {
        if (!location.state?.startBookingFresh || !companyUuid) return;
        try {
            sessionStorage.removeItem(`public-booking-draft:${companyUuid}`);
        } catch {
            // A navegação ainda começa no início mesmo quando o navegador bloqueia o storage.
        }
        navigate(location.pathname, { replace: true, state: null });
    }, [companyUuid, location.pathname, location.state, navigate]);

    // Rota é a fonte da etapa atual; rascunho mantém as escolhas entre telas e após atualizar.
    useEffect(() => {
        if (!companyUuid) return;
        const route = STEP_ROUTE[currentStep] || STEP_ROUTE[STEPS.CUSTOMER];
        if (isConfirmedRoute && appointmentResult) return;
        if (routeStep !== route) {
            navigate(`/booking/${companyUuid}/${route}`, { replace: true });
        }
    }, [companyUuid, currentStep, isConfirmedRoute, appointmentResult, navigate, routeStep]);

    useEffect(() => {
        if (!companyUuid) return;
        try {
            sessionStorage.setItem(`public-booking-draft:${companyUuid}`, JSON.stringify({
                savedAt: Date.now(),
                currentStep,
                selectedBranch,
                selectedServices,
                selectedEmployee,
                selectedDate: selectedDate?.toISOString() || null,
                selectedTime,
                customerData,
                appointmentResult
            }));
        } catch {
            // Se o navegador bloquear armazenamento, o fluxo atual ainda funciona normalmente.
        }
    }, [companyUuid, currentStep, selectedBranch, selectedServices, selectedEmployee,
        selectedDate, selectedTime, customerData, appointmentResult]);

    // Carrega informações da empresa
    useEffect(() => {
        // Aceita slug (URL amigável) ou uuid (links antigos). O backend resolve os dois
        // em getCompany; identificador inexistente cai no erro de "empresa não encontrada".
        if (!companyUuid) {
            setError(t('booking.invalid_link'));
            setLoading(false);
            return;
        }

        loadCompanyInfo();
    }, [companyUuid]);

    // Injeta manifest e apple-touch-icon dinâmicos para que, ao salvar o atalho
    // no smartphone, o ícone exibido seja a logo do tenant em vez do ícone genérico.
    // Android Chrome lê o <link rel="manifest"> (blob URL); iOS Safari lê o apple-touch-icon.
    useEffect(() => {
        if (!companyInfo) return;

        // Escopo isolado do portal /p/{slug}/, cujo worker também gerencia Web Push.
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/booking-service-worker.js', { scope: '/booking/' })
                .catch((registrationError) => {
                    console.warn('Não foi possível preparar o agendamento para instalação.', registrationError);
                });
        }

        const logoUrl = companyInfo.logoUrl
            ? resolvePublicAssetUrl(companyInfo.logoUrl)
            : null;
        const startUrl = window.location.href;
        const bookingScope = `${window.location.origin}/booking/`;

        // --- Manifest dinâmico (Android Chrome) ---
        const manifest = {
            name: companyInfo.name,
            short_name: companyInfo.name.slice(0, 12),
            id: startUrl,
            scope: bookingScope,
            icons: logoUrl
                ? [
                    { src: logoUrl, sizes: '192x192', purpose: 'any maskable' },
                    { src: logoUrl, sizes: '512x512', purpose: 'any maskable' }
                  ]
                : [
                    { src: `${window.location.origin}/web-app-manifest-192x192.png`, sizes: '192x192', type: 'image/png' },
                    { src: `${window.location.origin}/web-app-manifest-512x512.png`, sizes: '512x512', type: 'image/png' }
                  ],
            start_url: startUrl,
            display: 'standalone',
            theme_color: companyInfo.headerColor || '#000000',
            background_color: '#ffffff'
        };

        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        const blobUrl = URL.createObjectURL(blob);

        let manifestLink = document.querySelector('link[rel="manifest"]');
        const prevManifestHref = manifestLink ? manifestLink.getAttribute('href') : null;
        if (!manifestLink) {
            manifestLink = document.createElement('link');
            manifestLink.rel = 'manifest';
            document.head.appendChild(manifestLink);
        }
        manifestLink.setAttribute('href', blobUrl);

        // --- Apple Touch Icon (iOS — ignora mudanças no manifest via JS) ---
        let appleIconLink = document.querySelector('link[rel="apple-touch-icon"]');
        const prevAppleHref = appleIconLink ? appleIconLink.getAttribute('href') : null;
        if (logoUrl) {
            if (!appleIconLink) {
                appleIconLink = document.createElement('link');
                appleIconLink.rel = 'apple-touch-icon';
                document.head.appendChild(appleIconLink);
            }
            appleIconLink.setAttribute('href', logoUrl);
        }

        // --- Meta tags iOS: iOS Safari prioriza apple-mobile-web-app-title sobre <title>
        // ao nomear o atalho na tela inicial, então sem isso o nome ficava genérico ("Colmeia"). ---
        const metas = [
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-title', content: companyInfo.name },
            { name: 'theme-color', content: companyInfo.headerColor || '#000000' }
        ];
        const createdMetas = [];
        const prevMetaContent = {};
        metas.forEach(({ name, content }) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (tag) {
                prevMetaContent[name] = tag.getAttribute('content');
            } else {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
                createdMetas.push(tag);
            }
            tag.setAttribute('content', content);
        });

        // --- Título da aba ---
        const prevTitle = document.title;
        document.title = companyInfo.name;

        return () => {
            URL.revokeObjectURL(blobUrl);
            if (manifestLink) {
                if (prevManifestHref) manifestLink.setAttribute('href', prevManifestHref);
                else manifestLink.remove();
            }
            if (appleIconLink && prevAppleHref) appleIconLink.setAttribute('href', prevAppleHref);
            Object.entries(prevMetaContent).forEach(([name, content]) => {
                const tag = document.querySelector(`meta[name="${name}"]`);
                if (tag && content != null) tag.setAttribute('content', content);
            });
            createdMetas.forEach((tag) => tag.remove());
            document.title = prevTitle;
        };
    }, [companyInfo]);

    // Ao voltar de "Meus agendamentos", a rota é montada novamente. Recupera o cliente salvo
    // nesta sessão para que ele não precise repetir a identificação por CPF.
    useEffect(() => {
        if (!companyInfo || !companyUuid || currentStep !== STEPS.CUSTOMER) return;

        const storedDocument = getStoredCustomerDocument(companyUuid);
        if (!storedDocument) return;

        let cancelled = false;
        lookupCustomerByDocument(companyUuid, storedDocument)
            .then((response) => {
                if (cancelled) return;
                const customer = response.data || {};
                const fullName = customer.fullName || `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
                const nameParts = fullName.split(/\s+/).filter(Boolean);
                setCustomerData({
                    firstName: customer.firstName || nameParts[0] || '',
                    lastName: customer.lastName || nameParts.slice(1).join(' '),
                    phone: customer.phone || '',
                    email: customer.email || '',
                    document: storedDocument,
                    ...lookupStatusFields(customer)
                });
                // O CPF salvo pode voltar a identificar alguém que está com mensalidade em
                // aberto; nesse caso mantém a pessoa na identificação, sem restaurar o wizard.
                if (customer.clubPaymentOverdue) return;
                const nextStep = companyInfo.branches?.length === 1 ? STEPS.SERVICE : STEPS.BRANCH;
                navigate(`/booking/${companyUuid}/${STEP_ROUTE[nextStep]}`);
            })
            .catch((error) => {
                // Uma falha de rede não significa que o cliente deixou de existir.
                if (error.response?.status === 404) clearStoredCustomerDocument(companyUuid);
            });

        return () => { cancelled = true; };
    }, [companyInfo, companyUuid, currentStep]);

    const loadCompanyInfo = async () => {
        try {
            setLoading(true);
            const response = await getCompanyInfo(companyUuid);
            setCompanyInfo(response.data);

            // Se só tem uma filial, seleciona automaticamente. O passo continua sendo a
            // identificação, que agora abre o fluxo; quem pula a filial é handleCustomerSubmit.
            if (response.data.branches && response.data.branches.length === 1) {
                setSelectedBranch(response.data.branches[0]);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setError(t('booking.company_not_found'));
            } else {
                setError(t('booking.load_error'));
            }
        } finally {
            setLoading(false);
        }
    };

    // Calcula a duração total dos serviços selecionados
    const getTotalDuration = () => {
        return selectedServices.reduce((total, service) => total + (service.duration || 30), 0);
    };

    // Calcula o preço total
    const getTotalPrice = () => {
        return selectedServices.reduce((total, service) => total + (service.price || 0), 0);
    };

    const hasSingleBranch = companyInfo?.branches?.length === 1;

    // A URL identifica a etapa, e todas as formas de avançar/voltar usam o mesmo mapa.
    const navigateToStep = (step, options = {}) => {
        const route = STEP_ROUTE[step] || STEP_ROUTE[STEPS.CUSTOMER];
        navigate(`/booking/${companyUuid}/${route}`, options);
    };

    const workspaceRef = useRef(null);
    const previousRouteStepRef = useRef(routeStep);

    // Ao trocar de etapa a janela mantinha a rolagem: no celular, quem tocava em "Confirmar" no
    // fim da revisão via primeiro o rodapé da tela de sucesso, e não o check de confirmação. A
    // rolagem para no início da área do agendamento, e não no topo da página, para o banner não
    // empurrar a etapa para baixo. Só rola quando esse início ficou acima da tela, assim etapas
    // curtas, já à vista, não dão salto.
    useEffect(() => {
        if (previousRouteStepRef.current === routeStep) return;
        previousRouteStepRef.current = routeStep;

        const workspaceTop = workspaceRef.current?.getBoundingClientRect().top;
        if (workspaceTop == null || workspaceTop >= 0) return;
        // A folga evita que o conteúdo encoste na borda superior da tela.
        window.scrollTo({ top: window.scrollY + workspaceTop - 12 });
    }, [routeStep]);

    useEffect(() => {
        if (!companyInfo || loading || currentStep === STEPS.CUSTOMER
            || (isConfirmedRoute && appointmentResult)) return;

        // Rascunhos e URLs diretas não podem reabrir o wizard para assinante inadimplente.
        if (customerData.clubPaymentOverdue) {
            navigateToStep(STEPS.CUSTOMER, { replace: true });
            return;
        }

        // URLs diretas ou rascunhos incompletos voltam à primeira etapa sem contexto suficiente.
        let firstIncompleteStep = STEPS.CONFIRMATION;
        if (!customerData.firstName || !customerData.phone || !customerData.document) {
            firstIncompleteStep = STEPS.CUSTOMER;
        } else if (!selectedBranch && !hasSingleBranch) {
            firstIncompleteStep = STEPS.BRANCH;
        } else if (!selectedServices.length) {
            firstIncompleteStep = STEPS.SERVICE;
        } else if (!selectedEmployee) {
            firstIncompleteStep = STEPS.EMPLOYEE;
        } else if (!selectedDate || !selectedTime) {
            firstIncompleteStep = STEPS.DATETIME;
        }

        if (currentStep > firstIncompleteStep) {
            navigateToStep(firstIncompleteStep, { replace: true });
        }
    }, [companyInfo, loading, currentStep, customerData, selectedBranch, selectedServices,
        selectedEmployee, selectedDate, selectedTime, hasSingleBranch,
        isConfirmedRoute, appointmentResult]);

    // A identificação é a porta de entrada, não uma etapa do agendamento: enquanto ela está na
    // tela, o cliente ainda não escolheu nada, então barra de progresso, tira de contexto e
    // etapas só mostrariam campos vazios e um wizard que ele nem começou.
    const isIdentifying = currentStep === STEPS.CUSTOMER;

    // Navegação do wizard
    const nextStep = () => {
        navigateToStep(Math.min(currentStep + 1, STEPS.CONFIRMATION));
    };

    const prevStep = () => {
        // Volta pulando a filial quando ela foi escolhida sozinha, espelhando a ida.
        const previous = currentStep - 1;
        navigateToStep(previous === STEPS.BRANCH && hasSingleBranch
            ? STEPS.CUSTOMER
            : Math.max(previous, STEPS.CUSTOMER));
    };

    // Handlers de seleção
    const handleSelectBranch = (branch) => {
        setSelectedBranch(branch);
        setSelectedServices([]);
        setSelectedEmployee(null);
        setSelectedDate(null);
        setSelectedTime(null);
        nextStep();
    };

    const handleSelectServices = (services) => {
        setSelectedServices(services);
        setSelectedEmployee(null);
        setSelectedDate(null);
        setSelectedTime(null);
        nextStep();
    };

    const handleSelectEmployee = (employee) => {
        setSelectedEmployee(employee);
        setSelectedDate(null);
        setSelectedTime(null);
        nextStep();
    };

    const handleSelectDateTime = (date, time, assignedEmployee = null) => {
        setSelectedDate(date);
        setSelectedTime(time);
        if (selectedEmployee?.isRandom && assignedEmployee) {
            setSelectedEmployee((previous) => ({
                ...previous,
                assignedEmployee
            }));
        }
        nextStep();
    };

    const handleCustomerSubmit = (data) => {
        setCustomerData(data);
        if (data.document) {
            setStoredCustomerDocument(companyUuid, data.document);
        }
        // A identificação já informa a pendência; evite levar o cliente até a etapa final
        // para só então negar o agendamento.
        if (data.clubPaymentOverdue) return;
        // Com uma filial só não há o que escolher: vai direto para os serviços, já com o
        // benefício do clube aplicado na lista.
        navigateToStep(hasSingleBranch ? STEPS.SERVICE : STEPS.BRANCH);
    };

    const handleAppointmentCreated = (result) => {
        setAppointmentResult(result);
        // Grava antes de trocar de URL para que atualizar a tela de sucesso recupere o agendamento.
        try {
            sessionStorage.setItem(`public-booking-draft:${companyUuid}`, JSON.stringify({
                savedAt: Date.now(),
                currentStep: STEPS.CONFIRMATION,
                selectedBranch,
                selectedServices,
                selectedEmployee,
                selectedDate: selectedDate?.toISOString() || null,
                selectedTime,
                customerData,
                appointmentResult: result
            }));
        } catch {
            // A confirmação permanece visível; o armazenamento só mantém os dados ao atualizar.
        }
        navigate(`/booking/${companyUuid}/confirmation`);
    };

    const handleNewAppointment = () => {
        navigateToStep(STEPS.CUSTOMER);
        setSelectedBranch(hasSingleBranch ? companyInfo.branches[0] : null);
        setSelectedServices([]);
        setSelectedEmployee(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setCustomerData({ firstName: '', lastName: '', phone: '', document: '' });
        setAppointmentResult(null);
    };

    const handleChangeCustomer = () => {
        clearStoredCustomerDocument(companyUuid);
        navigateToStep(STEPS.CUSTOMER);
        setSelectedBranch(hasSingleBranch ? companyInfo.branches[0] : null);
        setSelectedServices([]);
        setSelectedEmployee(null);
        setSelectedDate(null);
        setSelectedTime(null);
        setCustomerData({ firstName: '', lastName: '', phone: '', document: '' });
        setAppointmentResult(null);
    };

    // A filial única não aparece como etapa: o progresso precisa refletir exatamente o que
    // ainda existe para o cliente escolher, principalmente no celular.
    const bookingSteps = [
        !hasSingleBranch && { step: STEPS.BRANCH, name: t('booking.step_branch') },
        { step: STEPS.SERVICE, name: t('booking.step_service') },
        { step: STEPS.EMPLOYEE, name: t('booking.step_professional') },
        { step: STEPS.DATETIME, name: t('booking.step_datetime') }
    ].filter(Boolean);
    const currentProgressIndex = bookingSteps.findIndex(({ step }) => step === currentStep);
    const currentProgressStep = bookingSteps[currentProgressIndex];

    // Migalhas do que já foi escolhido. Só entra o que existe: a régua de etapas já mostra o
    // que falta, então repetir "Profissional" vazio aqui seria ruído.
    const bookingSummary = [
        selectedBranch && { icon: 'fa-location-dot', label: selectedBranch.name },
        selectedServices.length > 0 && {
            icon: 'fa-list-check',
            label: `${selectedServices.length} ${selectedServices.length === 1 ? 'serviço' : 'serviços'}`
        },
        selectedEmployee && { icon: 'fa-user', label: selectedEmployee.name },
        selectedDate && selectedTime && {
            icon: 'fa-clock',
            label: `${formatBookingContextDate(selectedDate)} às ${selectedTime}`
        }
    ].filter(Boolean);

    // Renderiza loading
    if (loading) {
        return (
            <div className="public-booking-page">
                <Container className="py-5">
                    <div className="text-center">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-3 text-muted">{t('booking.loading')}</p>
                    </div>
                </Container>
            </div>
        );
    }

    // Renderiza erro
    if (error) {
        return (
            <div className="public-booking-page">
                <Container className="py-5">
                    <div className="text-center mb-4">
                        <img src={logo} alt="Logo" className="booking-logo" />
                    </div>
                    <Alert variant="danger" className="text-center">
                        <i className="fa fa-exclamation-circle me-2"></i>
                        {error}
                    </Alert>
                </Container>
            </div>
        );
    }

    // Destaques do fluxo (seleções, calendário, badges) seguem a cor da marca (headerColor),
    // derivando tons/transparências via color-mix. Sem cor configurada, cai num cinza neutro (#333).
    const brandColor = companyInfo?.headerColor || '#333';
    const brandVars = {
        '--booking-primary': brandColor,
        '--booking-primary-strong': `color-mix(in srgb, ${brandColor} 80%, #000)`,
        '--booking-primary-soft': `color-mix(in srgb, ${brandColor} 12%, #fff)`,
        '--booking-accent': brandColor,
        '--booking-accent-strong': `color-mix(in srgb, ${brandColor} 80%, #000)`,
        '--booking-accent-soft': `color-mix(in srgb, ${brandColor} 12%, #fff)`,
        '--rgba-secondary-1': `color-mix(in srgb, ${brandColor} 16%, transparent)`,
        '--rgba-secondary-2': `color-mix(in srgb, ${brandColor} 28%, transparent)`,
        '--rgba-secondary-3': `color-mix(in srgb, ${brandColor} 42%, transparent)`,
    };

    return (
        <div className="public-booking-page" style={brandVars}>
            <div
                className="booking-hero"
                style={getHeroStyle(companyInfo)}
            >
                {companyInfo?.bannerUrl && (
                    <div
                        className="booking-hero-banner-layer"
                        aria-hidden="true"
                        style={{
                            backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.55), rgba(15, 23, 42, 0.55)), url(${resolvePublicAssetUrl(companyInfo.bannerUrl)})`,
                            backgroundPosition: `${companyInfo?.bannerPositionX ?? 50}% ${companyInfo?.bannerPositionY ?? 50}%`,
                            backgroundSize: 'cover',
                            transform: `scale(${(companyInfo?.bannerZoom ?? 100) / 100})`,
                            transformOrigin: `${companyInfo?.bannerPositionX ?? 50}% ${companyInfo?.bannerPositionY ?? 50}%`,
                        }}
                    />
                )}
                <div className="booking-hero-content booking-hero-content--compact">
                    <div className="booking-hero-brand">
                        {shouldShowBookingLogo(companyInfo) && (
                            <div className="booking-hero-logo">
                                {companyInfo?.logoUrl ? (
                                    <ImageWithSkeleton src={resolvePublicAssetUrl(companyInfo.logoUrl)} alt={companyInfo.name} fill onDark />
                                ) : <img src={logo} alt="Colmeia Barber" />}
                            </div>
                        )}
                        {shouldShowBookingName(companyInfo) && <span className="booking-hero-brand__name">{companyInfo?.name}</span>}
                    </div>
                    <div className="booking-hero-copy">
                        <h1 className="booking-hero-name">Agendamento online</h1>
                        <p className="booking-hero-sub">{companyInfo?.heroSubtitle || t('booking.schedule_appointment')}</p>
                    </div>
                </div>
            </div>

            <Container className="booking-container">
                <AddBookingToHomeScreenHint companyUuid={companyUuid} />
                <div ref={workspaceRef} className={`booking-workspace${isIdentifying ? ' booking-workspace--identify' : ''}`}>
                    {!isIdentifying && (
                        <div className="booking-userbar">
                            {customerData.firstName ? (
                                <div className="booking-userbar__who">
                                    <span className="booking-userbar__greeting">
                                        {getGreeting()}, <strong>{customerData.firstName}</strong>
                                    </span>
                                    {customerData.subscriptionCustomer && (
                                        <span className="booking-userbar__club">
                                            <i className="fa fa-crown me-1"></i>
                                            {customerData.planName || 'Clube'}
                                        </span>
                                    )}
                                </div>
                            ) : <span />}

                            {/* Os agendamentos do cliente ficam na aba "Meus agendamentos" da barra
                                de abas; aqui sobra só a troca de cliente, discreta, como a saída de conta
                                de um aplicativo. */}
                            <div className="booking-userbar__actions">
                                <button
                                    type="button"
                                    className="booking-change-customer is-icon"
                                    onClick={handleChangeCustomer}
                                    aria-label="Sair e trocar cliente"
                                    title="Sair"
                                >
                                    <i className="fa fa-right-from-bracket"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Só o que já foi escolhido: rótulo de campo vazio ocupa espaço sem
                        informar, e a etapa atual já diz o que falta preencher. */}
                    {!isIdentifying && bookingSummary.length > 0 && (
                        <div className="booking-summary-line">
                            {bookingSummary.map((item, index) => (
                                <span key={item.label} className="booking-summary-line__item">
                                    {index > 0 && <i className="fa fa-circle booking-summary-line__dot"></i>}
                                    <i className={`fa ${item.icon}`}></i>
                                    {item.label}
                                </span>
                            ))}
                        </div>
                    )}

                {/* A pendência barra a entrada no wizard e continua visível para orientar
                    o cliente sobre o motivo e o próximo passo. */}
                {customerData?.clubPaymentOverdue && (
                    <Alert
                        variant="warning"
                        className="booking-overdue-alert mb-3"
                    >
                        <div className="booking-overdue-alert__head">
                            <i className="fa fa-triangle-exclamation"></i>
                            <div>
                                <strong>Mensalidade do clube em aberto</strong>
                                <span>Enquanto houver pendência, o agendamento pelo site fica indisponível.</span>
                            </div>
                        </div>

                        <div className="booking-overdue-alert__action">
                            {customerData.clubOverdueAmount > 0 && (
                                <div className="booking-overdue-alert__amount">
                                    <small>Valor em aberto</small>
                                    <strong>R$ {formatNumberWithTwoDecimals(customerData.clubOverdueAmount)}</strong>
                                </div>
                            )}
                        </div>

                        <p className="booking-overdue-alert__hint">
                            Entre em contato para regularizar e marcar seu horário.
                        </p>
                    </Alert>
                )}

                {/* Falta ou cancelamento em cima da hora barram a conclusão até a data informada.
                    Avisar já aqui poupa o cliente de montar o agendamento para levar o erro no
                    último clique; o texto, com a data de liberação, vem pronto do backend. */}
                {hasActiveOnlineBookingBlock(customerData) && !penaltyDismissed && (
                    <Alert
                        variant="warning"
                        className="mb-3"
                        dismissible
                        onClose={() => setPenaltyDismissed(true)}
                    >
                        <i className="fa fa-triangle-exclamation me-2"></i>
                        <strong>Agendamento pelo site indisponível no momento.</strong>{' '}
                        {customerData.onlineBookingBlockMessage}
                    </Alert>
                )}

                {/* Plano com dias definidos: avisa desde a identificação e continua à vista
                    durante a escolha da data. Não é bloqueio — em outros dias ele agenda
                    normalmente, só sem o benefício integral. */}
                {customerData?.planBenefitDays?.length > 0 && currentStep !== STEPS.CONFIRMATION
                    && !benefitDaysDismissed && (
                    <Alert
                        variant="info"
                        className="mb-3"
                        dismissible
                        onClose={() => setBenefitDaysDismissed(true)}
                    >
                        <i className="fa fa-circle-info me-2"></i>
                        Seu plano dá benefício em: <strong>{customerData.planBenefitDays.join(', ')}</strong>.
                        Nos outros dias você continua podendo agendar, com o valor de cada serviço ajustado.
                    </Alert>
                )}

                {/* Card Principal */}
                <Card className="booking-card">
                    {/* Régua dentro do cartão: é o único indicador de progresso que sobrou, e
                        colada ao conteúdo ela funciona como cabeçalho da etapa. */}
                    {currentStep < STEPS.CONFIRMATION && !isIdentifying && currentProgressStep && (
                        <>
                            <div className="booking-progress-heading">
                                <div>
                                    <span className="booking-progress-eyebrow">Agendamento</span>
                                    <strong>{currentProgressStep.name}</strong>
                                </div>
                                <span className="booking-progress-count">
                                    Passo {currentProgressIndex + 1} de {bookingSteps.length}
                                </span>
                            </div>
                            <div className="booking-steps" role="list" aria-label="Etapas do agendamento">
                            {bookingSteps.map(({ step, name }, index) => {
                                const done = index < currentProgressIndex;
                                return (
                                    <button
                                        key={name}
                                        type="button"
                                        role="listitem"
                                        className={`booking-steps__item${index === currentProgressIndex ? ' is-current' : ''}${done ? ' is-done' : ''}`}
                                        onClick={() => done && navigateToStep(step)}
                                        disabled={!done}
                                        aria-current={index === currentProgressIndex ? 'step' : undefined}
                                    >
                                        <span className="booking-steps__marker">
                                            {done ? <i className="fa fa-check"></i> : index + 1}
                                        </span>
                                        <span className="booking-steps__name">{name}</span>
                                    </button>
                                );
                            })}
                            </div>
                        </>
                    )}
                    <Card.Body className="booking-card-body">
                        {/* Step: Filial */}
                        {currentStep === STEPS.BRANCH && (
                            <StepBranch
                                branches={companyInfo?.branches || []}
                                onSelect={handleSelectBranch}
                            />
                        )}

                        {/* Step: Serviços */}
                        {currentStep === STEPS.SERVICE && (
                            <StepService
                                companyUuid={companyUuid}
                                branchId={selectedBranch?.id}
                                onSelect={handleSelectServices}
                                onBack={prevStep}
                                selectedServices={selectedServices}
                                customerData={customerData}
                            />
                        )}

                        {/* Step: Profissional */}
                        {currentStep === STEPS.EMPLOYEE && (
                            <StepEmployee
                                companyUuid={companyUuid}
                                branchId={selectedBranch?.id}
                                onSelect={handleSelectEmployee}
                                onBack={prevStep}
                                selectedEmployee={selectedEmployee}
                                customer={customerData}
                                selectedServices={selectedServices}
                            />
                        )}

                        {/* Step: Data e Hora */}
                        {currentStep === STEPS.DATETIME && (
                            <StepDateTime
                                companyUuid={companyUuid}
                                branchId={selectedBranch?.id}
                                employeeId={selectedEmployee?.id}
                                employee={selectedEmployee}
                                duration={getTotalDuration()}
                                onSelect={handleSelectDateTime}
                                onBack={prevStep}
                                selectedDate={selectedDate}
                                selectedTime={selectedTime}
                                /* Assinante identificado traz os dias do plano já resolvidos pelo
                                   backend; sem identificação vale a janela geral da empresa. */
                                bookingDays={customerData?.publicBookingDays || companyInfo?.publicBookingDays}
                                /* Lidas da resposta atual da empresa, e não da filial guardada no
                                   rascunho: um feriado cadastrado depois do rascunho precisa aparecer. */
                                specialDays={companyInfo?.branches?.find((branch) => branch.id === selectedBranch?.id)?.specialDays || []}
                            />
                        )}

                        {/* Step: Dados do Cliente */}
                        {currentStep === STEPS.CUSTOMER && (
                            <StepCustomerInfo
                                companyUuid={companyUuid}
                                customerData={customerData}
                                onSubmit={handleCustomerSubmit}
                                onBack={null}
                            />
                        )}

                        {/* Step: Confirmação */}
                        {currentStep === STEPS.CONFIRMATION && (
                            <StepConfirmation
                                companyUuid={companyUuid}
                                companyInfo={companyInfo}
                                branch={selectedBranch}
                                services={selectedServices}
                                employee={selectedEmployee}
                                date={selectedDate}
                                time={selectedTime}
                                customerData={customerData}
                                totalDuration={getTotalDuration()}
                                totalPrice={getTotalPrice()}
                                appointmentResult={appointmentResult}
                                onConfirm={handleAppointmentCreated}
                                onBack={prevStep}
                            />
                        )}
                    </Card.Body>
                </Card>
                </div>

                <PublicBrandSignature />
            </Container>
            {/* O rascunho é salvo na sessão, permitindo visitar o Clube e retomar o agendamento. */}
            <ClientTabBar companyUuid={companyUuid}
                onBookingTab={appointmentResult ? handleNewAppointment : undefined} />
        </div>
    );
};

export default PublicBookingPage;
